import html2canvas from 'html2canvas';
import { buildSitePlanImageProxyUrl, resolveSitePlanTextureUrls } from '../../services/api';

export interface CompareSitePlanMarker {
  left: number;
  top: number;
  width: number;
  height: number;
  label: string;
}

const blobToDataUrl = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error ?? new Error('Failed to read image'));
    reader.readAsDataURL(blob);
  });

// Firebase Storage answers with `Access-Control-Allow-Origin: *`, which the
// browser rejects for a credentialed request — so storage hosts are fetched
// without cookies, and only our own proxy (behind the agent session) with them.
const fetchImageAsDataUrl = async (url: string, credentials: RequestCredentials): Promise<string> => {
  const response = await fetch(url, { mode: 'cors', credentials });
  if (!response.ok) throw new Error(`Image fetch failed (${response.status})`);
  const blob = await response.blob();
  if (/^(text\/|application\/json)/i.test(blob.type)) throw new Error(`Response is not an image (${blob.type})`);
  const dataUrl = await blobToDataUrl(blob);
  // Make sure it decodes before it replaces a working <img>.
  await loadImageElement(dataUrl);
  return dataUrl;
};

/**
 * Site plans still served from HIP's media host send no CORS headers, so
 * html2canvas silently drops them from the capture (only the marker made it
 * into the PDF). Read the bytes through the same-origin site-plan proxy and
 * hand back a data URL that can be inlined into the capture instead.
 */
export const loadSitePlanImageDataUrl = async (
  imageUrl: string,
  projectId: string | null | undefined,
): Promise<string | null> => {
  const proxyUrl = buildSitePlanImageProxyUrl(projectId);
  const { primary, fallback } = resolveSitePlanTextureUrls(imageUrl, proxyUrl);
  for (const url of [primary, fallback]) {
    if (!url) continue;
    try {
      return await fetchImageAsDataUrl(url, url === proxyUrl ? 'include' : 'omit');
    } catch (err) {
      console.warn('Site plan image could not be inlined for PDF:', url, err);
    }
  }
  return null;
};

const waitForImage = (img: HTMLImageElement): Promise<void> =>
  new Promise((resolve) => {
    if (img.complete && img.naturalWidth > 0) {
      resolve();
      return;
    }
    img.onload = () => resolve();
    img.onerror = () => resolve();
  });

/**
 * Screenshots a compare panel. `inlineImages` maps an `<img>` src to a data URL
 * that replaces it in the cloned DOM, so cross-origin images still render.
 */
export const captureComparePanel = async (
  el: HTMLElement,
  inlineImages?: Map<string, string>,
): Promise<string> => {
  const canvas = await html2canvas(el, {
    useCORS: true,
    scale: 2,
    backgroundColor: '#ffffff',
    logging: false,
    onclone: async (_doc, clonedEl) => {
      if (!inlineImages?.size) return;
      const pending: Array<Promise<void>> = [];
      clonedEl.querySelectorAll('img').forEach((img) => {
        const replacement = inlineImages.get(img.getAttribute('src') ?? '');
        if (!replacement) return;
        img.removeAttribute('srcset');
        img.removeAttribute('crossorigin');
        img.src = replacement;
        pending.push(waitForImage(img));
      });
      await Promise.all(pending);
    },
  });
  return canvas.toDataURL('image/png');
};

const loadImageElement = (src: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Image load failed'));
    img.src = src;
  });

/**
 * Draws a whole site plan with the unit's stack marker, for exports where the
 * site plan panel is not on screen to be captured. Rotation and flip follow the
 * compare panel (`scaleX(-1) rotate(deg)`).
 */
export const renderSitePlanWithMarker = async (
  imageDataUrl: string,
  marker: CompareSitePlanMarker | null,
  transform: { rotationDegrees?: number; flipHorizontal?: boolean } = {},
): Promise<string> => {
  const img = await loadImageElement(imageDataUrl);
  const maxSide = 2400;
  const scale = Math.min(1, maxSide / Math.max(img.naturalWidth, img.naturalHeight));
  const w = Math.round(img.naturalWidth * scale);
  const h = Math.round(img.naturalHeight * scale);

  const plan = document.createElement('canvas');
  plan.width = w;
  plan.height = h;
  const pctx = plan.getContext('2d');
  if (!pctx) throw new Error('Canvas unavailable');
  pctx.fillStyle = '#ffffff';
  pctx.fillRect(0, 0, w, h);
  pctx.drawImage(img, 0, 0, w, h);

  if (marker) {
    const cx = ((marker.left + marker.width / 2) / 100) * w;
    const cy = ((marker.top + marker.height / 2) / 100) * h;
    const r = Math.max(14, Math.round(Math.min(w, h) * 0.018));
    pctx.beginPath();
    pctx.arc(cx, cy, r, 0, Math.PI * 2);
    pctx.fillStyle = '#2e7d32';
    pctx.fill();
    pctx.lineWidth = Math.max(2, r * 0.15);
    pctx.strokeStyle = '#ffffff';
    pctx.stroke();
    pctx.fillStyle = '#ffffff';
    pctx.font = `bold ${Math.round(r * 0.95)}px sans-serif`;
    pctx.textAlign = 'center';
    pctx.textBaseline = 'middle';
    pctx.fillText(marker.label, cx, cy + 1);
  }

  const rotation = (((transform.rotationDegrees ?? 0) % 360) + 360) % 360;
  if (!rotation && !transform.flipHorizontal) return plan.toDataURL('image/png');

  const rad = (rotation * Math.PI) / 180;
  const outW = Math.round(Math.abs(w * Math.cos(rad)) + Math.abs(h * Math.sin(rad)));
  const outH = Math.round(Math.abs(w * Math.sin(rad)) + Math.abs(h * Math.cos(rad)));
  const out = document.createElement('canvas');
  out.width = outW;
  out.height = outH;
  const octx = out.getContext('2d');
  if (!octx) throw new Error('Canvas unavailable');
  octx.fillStyle = '#ffffff';
  octx.fillRect(0, 0, outW, outH);
  octx.translate(outW / 2, outH / 2);
  if (transform.flipHorizontal) octx.scale(-1, 1);
  octx.rotate(rad);
  octx.drawImage(plan, -w / 2, -h / 2);
  return out.toDataURL('image/png');
};
